import { parse } from "date-fns";
import { z } from "zod";

import { config } from "../../config";

const combineDateAndTime = (date: Date, time: string): Date => {
	return parse(time, "HH:mm", date);
};

const meetingFormBaseSchema = z.object({
	name: z.string().trim().min(1, "This is a required field").max(280, "Name cannot exceed 280 characters"),
	startDate: z.date({ error: (issue) => (issue.input === undefined ? "This is a required field" : undefined) }),
	endDate: z.date({ error: (issue) => (issue.input === undefined ? "This is a required field" : undefined) }),
	startTime: z
		.string()
		.regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format")
		.min(1, "This is a required field"),
	endTime: z
		.string()
		.regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format")
		.min(1, "This is a required field"),
	description: z
		.string()
		.trim()
		.max(
			config.meetingDescriptionMaxLength,
			`Descriptions cannot exceed ${config.meetingDescriptionMaxLength} characters`
		)
		.optional()
		.or(z.literal("")),
	carryOver: z.boolean().default(false),
	addToCalendar: z.boolean().default(false),
	workspaceId: z.string().optional(),
});

const dateOrderingRefinement = {
	handler: (data: z.infer<typeof meetingFormBaseSchema>) => {
		const startDateTime = combineDateAndTime(data.startDate, data.startTime);
		const endDateTime = combineDateAndTime(data.endDate, data.endTime);
		return endDateTime > startDateTime;
	},
	options: {
		message: "End date must occur after the start date",
		path: ["endDate"],
	},
};

export const meetingFormSchema = meetingFormBaseSchema.refine(
	dateOrderingRefinement.handler,
	dateOrderingRefinement.options
);

export type MeetingFormSchema = z.infer<typeof meetingFormSchema>;

export type FormErrors = Partial<Record<keyof MeetingFormSchema, string>>;

export type MeetingValidationErrors = Partial<Record<keyof MeetingFormSchema, { message: string }>>;

export const dashboardMeetingFormSchema = meetingFormBaseSchema
	.extend({ workspaceId: z.string().min(1, "This is a required field") })
	.refine(dateOrderingRefinement.handler, dateOrderingRefinement.options);
